import { AIRCRAFT_TYPE_LIST, getAircraftType } from './AircraftCatalog.js';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const remainder = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainder}`;
};

const headingLabel = (radians) => {
  const degrees = ((-radians * 180 / Math.PI) % 360 + 360) % 360;
  return Math.round(degrees).toString().padStart(3, '0');
};

const options = (selected) => AIRCRAFT_TYPE_LIST.map((type) =>
  `<option value="${type.id}" ${type.id === selected ? 'selected' : ''}>${type.name} · ${type.role}</option>`
).join('');

export class GameUI {
  constructor(root, callbacks = {}, performanceProfile = {}) {
    this.root = root;
    this.callbacks = callbacks;
    this.performanceProfile = performanceProfile;
    this.toastTimer = null;
    this.render();
    this.cache();
    this.createRadarBackdrop();
    this.bind();
    this.updateAircraftCards();
  }

  render() {
    const radarSize = this.performanceProfile.radarSize ?? 360;
    this.root.innerHTML = `
      <div class="boot"><div class="boot__mark"><span class="boot__ring"></span><span>Preparing combat airspace</span></div></div>
      <div class="scanlines" aria-hidden="true"></div>

      <header class="topbar hud" data-hud>
        <div class="brand"><span class="brand__mark">AWS</span><div><b>AIRCRAFT WAR</b><em>SIMULATION</em></div></div>
        <div class="mission-clock"><span>BATTLE TIME</span><b data-time>00:00</b></div>
        <button class="icon-button" data-pause aria-label="Pause game"><span></span><span></span></button>
      </header>

      <section class="team-status hud" data-hud>
        <div class="team team--blue"><span>BLUE SQUADRON</span><b data-blue-count>5</b><i>ACTIVE</i></div>
        <div class="versus">5 <small>VS</small> 5</div>
        <div class="team team--red"><span>RED SQUADRON</span><b data-red-count>5</b><i>ACTIVE</i></div>
      </section>

      <section class="flight-data hud" data-hud aria-label="Flight data">
        <div><span>SPEED</span><b data-speed>000</b><small>KTS</small></div>
        <div><span>ALT</span><b data-altitude>0000</b><small>FT</small></div>
        <div><span>HDG</span><b data-heading>000</b><small>DEG</small></div>
        <div class="health"><span>HULL</span><b data-health>100</b><small>%</small><i><em data-health-bar></em></i></div>
      </section>

      <section class="weapons hud" data-hud>
        <div><span>CANNON</span><b>READY</b><small>SPACE</small></div>
        <div><span>MISSILES</span><b data-missiles>04</b><small>F</small></div>
        <div><span>SCORE</span><b data-score>00000</b><small>PTS</small></div>
      </section>

      <div class="combat-reticle hud" data-hud>
        <div class="lock-ring" data-lock-ring><i></i><i></i><i></i><i></i></div>
        <span data-target>SEARCHING FOR TARGET</span>
      </div>

      <section class="objective hud" data-hud>
        <span data-objective-label>SCRAMBLE</span>
        <b data-objective>Full throttle. Take off with your squadron.</b>
      </section>

      <section class="radar hud" data-hud>
        <div class="radar__label"><span>TACTICAL</span><b>ENEMY VECTOR</b></div>
        <canvas data-radar data-radar-mode="circular-pursuit" width="${radarSize}" height="${radarSize}"></canvas>
        <div class="radar__legend"><span><i></i>BLUE</span><span><i></i>RED</span></div>
      </section>

      <div class="warning" data-stall><b>STALL</b><span>LOWER NOSE · ADD POWER</span></div>
      <div class="toast" data-toast><span data-toast-title>Merge</span><b data-toast-points></b></div>

      <div class="touch-controls hud" data-hud>
        <div class="joystick" data-joystick><span>FLIGHT</span><i data-joystick-knob></i></div>
        <div class="touch-weapons">
          <button data-fire-gun><b>GUN</b><span>HOLD</span></button>
          <button data-fire-missile><b>MISSILE</b><span>FIRE</span></button>
        </div>
        <div class="touch-actions">
          <button data-hold="throttle:1"><b>+</b><span>THR</span></button>
          <button data-camera><b>◉</b><span>VIEW</span></button>
          <button data-hold="throttle:-1"><b>−</b><span>THR</span></button>
        </div>
      </div>

      <section class="intro panel" data-intro>
        <div class="intro__grid"></div>
        <div class="intro__content">
          <span class="kicker"><i></i> ALPINE THEATER · TWO SQUADRONS · ONE SKY</span>
          <h1>AIRCRAFT<br><span>WAR</span> SIMULATION</h1>
          <p>Command one aircraft inside a fully autonomous five-versus-five air battle. Choose the aircraft class for both squadrons, launch together, and own the sky.</p>
          <div class="squad-selectors">
            <label class="squad-card squad-card--blue">
              <span>YOUR SQUADRON · BLUE</span>
              <select data-blue-type>${options('falcon')}</select>
              <small data-blue-spec></small>
            </label>
            <label class="squad-card squad-card--red">
              <span>ENEMY SQUADRON · RED</span>
              <select data-red-type>${options('viper')}</select>
              <small data-red-spec></small>
            </label>
          </div>
          <button class="primary-button" data-start><span>LAUNCH BOTH SQUADRONS</span><i>↗</i></button>
          <div class="controls-legend">
            <span><kbd>W S</kbd>PITCH</span><span><kbd>A D</kbd>ROLL</span>
            <span><kbd>SHIFT CTRL</kbd>THROTTLE</span><span><kbd>SPACE</kbd>GUN</span><span><kbd>F</kbd>MISSILE</span>
          </div>
        </div>
        <aside><span>LIVE COMBAT SYSTEM</span><i></i><b>5V5</b></aside>
      </section>

      <section class="pause-panel panel" data-pause-panel>
        <span class="kicker">COMBAT SUSPENDED</span><h2>Paused</h2>
        <button class="primary-button" data-resume><span>RETURN TO BATTLE</span><i>↗</i></button>
        <button class="text-button" data-restart>Restart battle</button>
      </section>

      <section class="result-panel panel" data-result>
        <span class="kicker" data-result-kicker>AIRSPACE SECURED</span>
        <h2 data-result-title>Blue squadron wins.</h2>
        <p data-result-copy>Return to base. The sky is ours.</p>
        <div class="result-score"><span>COMBAT SCORE</span><b data-final-score>00000</b></div>
        <div class="result-stats">
          <div><span>TIME</span><b data-final-time>00:00</b></div>
          <div><span>BLUE</span><b data-final-blue>0</b></div>
          <div><span>RED</span><b data-final-red>0</b></div>
        </div>
        <button class="primary-button" data-fly-again><span>NEW BATTLE</span><i>↗</i></button>
      </section>

      <div class="renderer-chip" data-renderer>INITIALIZING</div>
      <div class="damage-flash" data-damage></div>
    `;
  }

  cache() {
    const find = (selector) => this.root.querySelector(selector);
    this.elements = {
      boot: find('.boot'), intro: find('[data-intro]'), pause: find('[data-pause-panel]'),
      result: find('[data-result]'), renderer: find('[data-renderer]'),
      blueType: find('[data-blue-type]'), redType: find('[data-red-type]'),
      blueSpec: find('[data-blue-spec]'), redSpec: find('[data-red-spec]'),
      time: find('[data-time]'), speed: find('[data-speed]'), altitude: find('[data-altitude]'),
      heading: find('[data-heading]'), health: find('[data-health]'), healthBar: find('[data-health-bar]'),
      missiles: find('[data-missiles]'), score: find('[data-score]'),
      blueCount: find('[data-blue-count]'), redCount: find('[data-red-count]'),
      target: find('[data-target]'), lockRing: find('[data-lock-ring]'),
      objectiveLabel: find('[data-objective-label]'), objective: find('[data-objective]'),
      radar: find('[data-radar]'), stall: find('[data-stall]'), damage: find('[data-damage]'),
      toast: find('[data-toast]'), toastTitle: find('[data-toast-title]'), toastPoints: find('[data-toast-points]'),
      resultKicker: find('[data-result-kicker]'), resultTitle: find('[data-result-title]'),
      resultCopy: find('[data-result-copy]'), finalScore: find('[data-final-score]'),
      finalTime: find('[data-final-time]'), finalBlue: find('[data-final-blue]'), finalRed: find('[data-final-red]'),
      hud: [...this.root.querySelectorAll('[data-hud]')]
    };
  }

  bind() {
    this.root.querySelector('[data-start]').addEventListener('click', () => this.callbacks.onStart?.({
      blueType: this.elements.blueType.value,
      redType: this.elements.redType.value
    }));
    this.elements.blueType.addEventListener('change', () => this.updateAircraftCards());
    this.elements.redType.addEventListener('change', () => this.updateAircraftCards());
    this.root.querySelector('[data-resume]').addEventListener('click', () => this.callbacks.onPause?.());
    this.root.querySelector('[data-restart]').addEventListener('click', () => this.callbacks.onRestart?.());
    this.root.querySelector('[data-fly-again]').addEventListener('click', () => this.returnToIntro());
  }

  updateAircraftCards() {
    const describe = (type) => `SPD ${type.maxSpeed} · TURN ${Math.round(type.turnRate * 100)} · HULL ${type.health}`;
    this.elements.blueSpec.textContent = describe(getAircraftType(this.elements.blueType.value));
    this.elements.redSpec.textContent = describe(getAircraftType(this.elements.redType.value));
  }

  ready(backend) {
    this.elements.renderer.textContent = backend;
    requestAnimationFrame(() => this.elements.boot.classList.add('ready'));
  }

  start() {
    this.root.classList.add('game-running');
    this.elements.intro.classList.add('is-hidden');
    this.elements.result.classList.remove('is-visible');
    this.elements.pause.classList.remove('is-visible');
    this.elements.hud.forEach((element) => element.classList.add('is-visible'));
  }

  returnToIntro() {
    this.root.classList.remove('game-running');
    this.elements.result.classList.remove('is-visible');
    this.elements.intro.classList.remove('is-hidden');
    this.elements.hud.forEach((element) => element.classList.remove('is-visible'));
    this.callbacks.onReturnToIntro?.();
  }

  setPaused(paused) {
    this.elements.pause.classList.toggle('is-visible', paused);
  }

  toast(title, detail = '', tone = 'blue') {
    this.elements.toastTitle.textContent = title;
    this.elements.toastPoints.textContent = detail;
    this.elements.toast.dataset.tone = tone;
    this.elements.toast.classList.remove('is-visible');
    void this.elements.toast.offsetWidth;
    this.elements.toast.classList.add('is-visible');
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.elements.toast.classList.remove('is-visible'), 1800);
  }

  flashDamage() {
    this.elements.damage.classList.remove('is-visible');
    void this.elements.damage.offsetWidth;
    this.elements.damage.classList.add('is-visible');
  }

  createRadarBackdrop() {
    const canvas = this.elements.radar;
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 7;
    const backdrop = document.createElement('canvas');
    backdrop.width = width;
    backdrop.height = height;
    const context = backdrop.getContext('2d');
    context.save();
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.clip();
    const background = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
    background.addColorStop(0, 'rgba(8,34,57,.94)');
    background.addColorStop(1, 'rgba(2,10,20,.96)');
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);
    context.strokeStyle = 'rgba(120,178,230,.12)';
    context.lineWidth = 1;
    for (const ringScale of [0.28, 0.55, 0.82]) {
      context.beginPath();
      context.arc(centerX, centerY, radius * ringScale, 0, Math.PI * 2);
      context.stroke();
    }
    context.beginPath();
    context.moveTo(centerX, centerY - radius);
    context.lineTo(centerX, centerY + radius);
    context.moveTo(centerX - radius, centerY);
    context.lineTo(centerX + radius, centerY);
    context.stroke();
    context.restore();
    context.strokeStyle = 'rgba(118,188,244,.4)';
    context.lineWidth = 2;
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.stroke();
    this.radarBackdrop = backdrop;
  }

  update(flight, battle, surface, target) {
    const player = battle.agents.find((agent) => agent.id === battle.playerId);
    const altitude = Math.max(0, Math.round((flight.position.y - surface.groundHeight) * 3.28084));
    const health = player ? Math.max(0, Math.round(player.health / player.maxHealth * 100)) : 0;
    const blue = battle.agents.filter((agent) => agent.alive && agent.team === 'blue').length;
    const red = battle.agents.filter((agent) => agent.alive && agent.team === 'red').length;
    this.elements.speed.textContent = Math.round(flight.speed * 1.94384).toString().padStart(3, '0');
    this.elements.altitude.textContent = altitude.toString().padStart(4, '0');
    this.elements.heading.textContent = headingLabel(flight.heading);
    this.elements.health.textContent = health;
    this.elements.healthBar.style.transform = `scaleX(${health / 100})`;
    this.elements.missiles.textContent = (player?.missiles ?? 0).toString().padStart(2, '0');
    this.elements.score.textContent = battle.score.toString().padStart(5, '0');
    this.elements.blueCount.textContent = blue;
    this.elements.redCount.textContent = red;
    this.elements.time.textContent = formatTime(battle.elapsed);
    this.elements.stall.classList.toggle('is-visible', flight.stall);
    this.elements.target.textContent = target ? `LOCK · ${target.callSign}` : 'SEARCHING FOR TARGET';
    this.elements.lockRing.classList.toggle('is-locked', Boolean(target));
    if (battle.phase === 'launch') {
      this.elements.objectiveLabel.textContent = 'SCRAMBLE';
      this.elements.objective.textContent = flight.grounded ? 'Full throttle. Take off with your squadron.' : 'Climb and form up. Enemy squadron inbound.';
    } else {
      this.elements.objectiveLabel.textContent = target ? `ENGAGE ${target.callSign}` : 'AIR SUPERIORITY';
      this.elements.objective.textContent = target ? 'Keep target inside the reticle. Fire when ready.' : 'Turn toward the red contacts on the tactical map.';
    }
    this.drawRadar(battle);
  }

  drawRadar(battle) {
    const canvas = this.elements.radar;
    const context = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - 7;
    const player = battle.agents.find((agent) => agent.id === battle.playerId);
    if (!player) return;
    const range = 15_500;
    context.clearRect(0, 0, width, height);
    context.drawImage(this.radarBackdrop, 0, 0);
    const cos = Math.cos(player.heading);
    const sin = Math.sin(player.heading);
    let nearestEnemy = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    let nearestLocalX = 0;
    let nearestLocalForward = 0;
    let contactCount = 0;
    context.save();
    context.beginPath();
    context.arc(centerX, centerY, radius, 0, Math.PI * 2);
    context.clip();
    for (const agent of battle.agents) {
      if (!agent.alive || agent.isPlayer) continue;
      contactCount += 1;
      const dx = agent.position.x - player.position.x;
      const dz = agent.position.z - player.position.z;
      const localX = dx * cos - dz * sin;
      const localForward = -dx * sin - dz * cos;
      const distance = Math.hypot(localX, localForward);
      const scale = Math.min(distance, range) / range * radius * 0.82;
      const divisor = distance || 1;
      const x = centerX + localX / divisor * scale;
      const y = centerY - localForward / divisor * scale;
      context.beginPath();
      context.arc(x, y, 5.2, 0, Math.PI * 2);
      context.fillStyle = agent.team === 'blue' ? '#3d9bff' : '#ff435a';
      context.shadowColor = context.fillStyle;
      context.shadowBlur = 9;
      context.fill();
      context.shadowBlur = 0;
      if (agent.team === 'red' && distance < nearestDistance) {
        nearestEnemy = agent;
        nearestDistance = distance;
        nearestLocalX = localX;
        nearestLocalForward = localForward;
      }
    }
    context.restore();
    context.save();
    context.translate(centerX, centerY);
    context.beginPath();
    context.moveTo(0, -11); context.lineTo(7, 8); context.lineTo(0, 4); context.lineTo(-7, 8); context.closePath();
    context.fillStyle = '#7fc5ff';
    context.shadowColor = '#3d9bff'; context.shadowBlur = 12; context.fill();
    context.restore();

    if (nearestEnemy) {
      const divisor = Math.hypot(nearestLocalX, nearestLocalForward) || 1;
      const arrowRadius = radius - 18;
      const arrowX = nearestLocalX / divisor * arrowRadius;
      const arrowY = -nearestLocalForward / divisor * arrowRadius;
      context.save();
      context.setLineDash([5, 7]);
      context.strokeStyle = 'rgba(255,202,103,.28)';
      context.lineWidth = 1.5;
      context.beginPath();
      context.moveTo(centerX, centerY);
      context.lineTo(centerX + arrowX * 0.82, centerY + arrowY * 0.82);
      context.stroke();
      context.restore();
      context.save();
      context.translate(centerX + arrowX, centerY + arrowY);
      context.rotate(Math.atan2(arrowY, arrowX) + Math.PI / 2);
      context.beginPath();
      context.moveTo(0, -12); context.lineTo(7, 7); context.lineTo(0, 4); context.lineTo(-7, 7); context.closePath();
      context.fillStyle = '#ffca67';
      context.shadowColor = '#ff8d36'; context.shadowBlur = 12; context.fill();
      context.restore();
      canvas.dataset.pursuitEnemy = nearestEnemy.id;
      canvas.dataset.pursuitDistance = Math.round(nearestDistance).toString();
      canvas.dataset.pursuitBearing = Math.atan2(arrowY, arrowX).toFixed(4);
    } else {
      canvas.dataset.pursuitEnemy = '';
      canvas.dataset.pursuitDistance = '0';
      canvas.dataset.pursuitBearing = '0';
    }
    canvas.dataset.contactCount = contactCount.toString();
  }

  showResult(battle, success) {
    const blue = battle.agents.filter((agent) => agent.alive && agent.team === 'blue').length;
    const red = battle.agents.filter((agent) => agent.alive && agent.team === 'red').length;
    this.elements.resultKicker.textContent = success ? 'AIRSPACE SECURED' : 'BLUE SQUADRON DEFEATED';
    this.elements.resultTitle.textContent = success ? 'Blue squadron wins.' : 'Red squadron owns the sky.';
    this.elements.resultCopy.textContent = success ? 'Return to base. The sky is ours.' : 'Rearm, choose your aircraft, and launch again.';
    this.elements.finalScore.textContent = battle.score.toString().padStart(5, '0');
    this.elements.finalTime.textContent = formatTime(battle.elapsed);
    this.elements.finalBlue.textContent = blue;
    this.elements.finalRed.textContent = red;
    this.elements.result.dataset.result = success ? 'success' : 'defeat';
    window.setTimeout(() => this.elements.result.classList.add('is-visible'), 650);
  }
}
